import { PAYMENT_PROMISE_STATUS_LABELS, type PaymentPromiseStatus } from '@chukua/common';
import { Button, Tag } from 'antd';
import clsx from 'clsx';
import { formatMoney } from '../../../../../shared/policies/format-money';
import type { PaymentPromiseView } from '../payment-promises.api';

const STATUS_COLOR: Record<PaymentPromiseStatus, 'default' | 'success' | 'error' | 'warning'> = {
  open: 'warning',
  kept: 'success',
  broken: 'error',
  superseded: 'default',
};

const formatDate = (date: string) => new Intl.DateTimeFormat('en-KE', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));

interface PromiseCardProps {
  promise: PaymentPromiseView;
  /** Absent when the viewer cannot record: replacing a promise is a new promise, so it needs the same permission. */
  onSupersede?: (promise: PaymentPromiseView) => void;
}

/** One promise: what was promised and by when, the invoices it covers, and for a decided one what the sweep found. A superseded promise stays, struck through. */
export function PromiseCard({ promise, onSupersede }: PromiseCardProps) {
  const isSuperseded = promise.status === 'superseded';
  return (
    <li className="rounded-xl border border-line bg-surface p-4">
      <div className={clsx('flex flex-wrap items-center gap-x-3 gap-y-1', isSuperseded && 'text-muted line-through')}>
        <Tag className="m-0" color={STATUS_COLOR[promise.status]}>
          {PAYMENT_PROMISE_STATUS_LABELS[promise.status]}
        </Tag>
        <span className="font-medium">{formatMoney({ amountMinor: promise.amountMinor, currency: promise.currency })}</span>
        <span>by {formatDate(promise.promisedDate)}</span>
        {promise.isDueToday && (
          <Tag className="m-0 no-underline" color="warning">
            Due today
          </Tag>
        )}
        {onSupersede && promise.status === 'open' && (
          <Button size="small" type="link" className="ml-auto no-underline" onClick={() => onSupersede(promise)}>
            Replace
          </Button>
        )}
      </div>
      {promise.outcome && (
        <p className="mt-2 mb-0 text-fg">
          {promise.outcome.receivedMinor === 0
            ? 'Nothing was paid.'
            : `${formatMoney({ amountMinor: promise.outcome.receivedMinor, currency: promise.currency })} of ${formatMoney({ amountMinor: promise.outcome.promisedMinor, currency: promise.currency })} was paid.`}
        </p>
      )}
      <p className="mt-2 mb-0 text-sm text-muted">
        {promise.invoices.length > 0 && (
          <span className="font-mono">{promise.invoices.map((invoice) => invoice.invoiceNumber ?? 'Unnumbered').join(', ')} · </span>
        )}
        Recorded by {promise.recordedBy.name}
      </p>
    </li>
  );
}
